// tslint:disable: no-inferrable-types
import { Workflow } from './workflow.model';
import { ok, err, Result} from 'neverthrow';
import { Transition } from './transition.model';

export class MermaidSerializer {
  private static NOT_FOUND = -1;

  // Minimal valid MermaidJS graph definition - empty graph
  private static EMPTY_GRAPH = 'graph TD\n';

  // Is provided `graph` a valid emtpy MermaidJS graph?
  public static isEmptyGraph(graph: string): boolean {
    return MermaidSerializer.EMPTY_GRAPH === graph;
  }

  /**
   * State & Transition name sanitisation
   *
   * If the names contain special characters which break Mermaid syntax we have
   * to sanitise the value.
   *
   * Simplest workaround is to enclose the name in double quetes and convert the
   * double quote to an entity.
   *
   * @see https://mermaid-js.github.io/mermaid/#/flowchart?id=special-characters-that-break-syntax
   * @param value Raw State or Transition name
   * @returns Sanitised safe Mermaid graph string
   */
  private static sanitise(value: string): string {
    return `"${value.replace(/"/g, '#quot;')}"`;
  }

  /**
   * Serializes Workflow object into MermaidJS graph definition
   *
   * @remarks
   *
   * **Example of valid MermaidJS graph definition:**
   * ```
   * graph TD
   * start(( ))-->|Apply|b1bgd04jac[NEW]
   * b1bgd04jac[NEW]-->|Application submission|45k7cbf41jv[WAITING]
   * 45k7cbf41jv[WAITING]-->|Application approval|h4l8x4v3rtn[APPROVED]
   * 45k7cbf41jv[WAITING]-->|Application rejection|dpdlthl47gc[REJECTED]
   * dpdlthl47gc[REJECTED]-->|Application change|45k7cbf41jv[WAITING]
   * h4l8x4v3rtn[APPROVED]-->|Cancelation by Staff|7ga9tn516c[CANCELED]
   * 45k7cbf41jv[WAITING]-->|Cancelation by Candidate|7ga9tn516c[CANCELED]
   * ```
   */
  public static serialize(workflow: Workflow, activeTransition?: Transition): Result<string, Error> {
    const statesWithoutTransitions = [...workflow.states];

    // Sanity checks
    if (workflow.states.length === 0 && workflow.transitions.length === 0) {
      return err(new Error($localize `Workflow is empty and has no transitions and states!`));
    }
    if (workflow.states.length === 0 && workflow.transitions.length > 0) {
      return err(new Error($localize `Workflow has no states has defined transitions!`));
    }

    // Initial transition
    let result = MermaidSerializer.EMPTY_GRAPH;
    if (workflow.initialTransition) {
      result = result + `${workflow.initialTransition.source.id}(( ))`;
      result = result + `-->|${MermaidSerializer.sanitise(workflow.initialTransition.name)}|`;
      result = result + `${workflow.initialTransition.target.id}[${MermaidSerializer.sanitise(workflow.initialTransition.target.name)}]`;
      result = result + '\n';
    }

    // Transition lines
    if (workflow.transitions) {
      workflow.transitions.forEach(transition => {
        const sourceIDX = statesWithoutTransitions.findIndex(s => s.id === transition.source.id);
        if (sourceIDX !== MermaidSerializer.NOT_FOUND) {
          statesWithoutTransitions.splice(sourceIDX, 1);
        }
        const targetIDX = statesWithoutTransitions.findIndex(s => s.id === transition.target.id);
        if (targetIDX !== MermaidSerializer.NOT_FOUND) {
          statesWithoutTransitions.splice(targetIDX, 1);
        }
        result = result + `${transition.source.id}[${MermaidSerializer.sanitise(transition.source.name)}]`;
        result = result + `-->|${MermaidSerializer.sanitise(transition.name)}|`;
        result = result + `${transition.target.id}[${MermaidSerializer.sanitise(transition.target.name)}]`;
        result = result + '\n';
      });
    }

    // Empty states
    if (statesWithoutTransitions.length > 0) {
      statesWithoutTransitions.forEach(state => {
        result = result + `${state.id}[${MermaidSerializer.sanitise(state.name)}]\n`;
      });
    }

    if (activeTransition) {
      const res = MermaidSerializer.highlightTransition(workflow, activeTransition);
      if (res.isErr()) { return res; }
      result = result + res.value;
    }
    return ok(result);
  }

  /**
   * Generates Mermaid markup to highlight States and Links in the flowchart
   *
   * @param workflow Workflow we are rendering graph for
   * @param transition Transition which should be highlighted.
   */
  private static highlightTransition(workflow: Workflow, transition: Transition): Result<string, Error> {
    // Deal with Initial transition and make sure Transition to be highlighted
    // is part of the workflow.
    let index = MermaidSerializer.NOT_FOUND;
    if (transition.isInitialTransition) {
      index = 0;
    } else {
      // We have to add +1 to index here because Mermaid indexes all rendered
      // links (which includes the Initial transition), but `workflow.transitions`
      // does not include Initial transition, therefore our transition index
      // would be -1 lower than expected.
      const idx = workflow.transitions.indexOf(transition);
      if (idx === MermaidSerializer.NOT_FOUND) {
        return err(new Error($localize `Transition to be highlighted is not part of the Workflow!`));
      }
      index = idx + 1;
    }

    let result: string = '';
    const stateIDs = [ transition.source.id, transition.target.id ];

    // State highlighting
    if (stateIDs.length > 0) {
      result = result + 'classDef active fill:#f96;\n';
      result = result + `class ${stateIDs.join(',')} active;\n`;
    }

    // Transition highlighting
    if (index !== MermaidSerializer.NOT_FOUND) {
      result = result + `linkStyle ${index} stroke:#f96,stroke-width:2px;\n`;
    }

    return ok(result);
  }

}
